import { getAnalyticsEnvironment, ANALYTICS_ENV } from "@core/analytics/getEnvironment";
import { safeSessionStorage } from "@shared/utils/safeStorage";

/** Query param that forces the Visa Dining Collection badge for staging demos. */
export const VISA_DINING_PREVIEW_PARAM = "previewVisaDining";

/** sessionStorage key — the captured param survives SPA step navigation. */
const STORAGE_KEY = "meitre.previewVisaDining";

/** Upper bound so a stray `?previewVisaDining=9999` can't badge every slot. */
const MAX_PREVIEW_SLOTS = 20;

/** Matches a slot hour like "20:00" or "9:30". */
const SLOT_TIME_RE = /^\d{1,2}:\d{2}$/;

/**
 * Which time slots should render the Visa Dining Collection badge under preview.
 * `count` badges the first N slots; `times` badges those exact slot hours
 * (e.g. "20:00"). Both empty means preview is off.
 */
export interface VisaDiningPreview {
  count: number;
  times: ReadonlySet<string>;
}

const PREVIEW_OFF: VisaDiningPreview = { count: 0, times: new Set() };

/**
 * Whether the preview override may run on the current host: local + staging
 * only, never production (meitre.com / *.v2.meitre.com).
 */
function previewAllowed(): boolean {
  return getAnalyticsEnvironment() !== ANALYTICS_ENV.PRODUCTION;
}

/**
 * Parse a raw param value:
 *  - "" or "true"            → first 1 slot
 *  - contains ":" e.g. "20:00,20:15,20:30" → those exact slot hours (HH:MM)
 *  - positive integer "3"    → first 3 slots (capped)
 *  - anything else / 0 / neg → off
 */
function parsePreview(raw: string): VisaDiningPreview {
  if (raw === "" || raw === "true") return { count: 1, times: new Set() };

  if (raw.includes(":")) {
    const times = raw.split(",").map((s) => s.trim()).filter((t) => SLOT_TIME_RE.test(t));
    return times.length > 0 ? { count: 0, times: new Set(times) } : PREVIEW_OFF;
  }

  const n = Number.parseInt(raw, 10);
  if (Number.isNaN(n) || n < 1) return PREVIEW_OFF;
  return { count: Math.min(n, MAX_PREVIEW_SLOTS), times: new Set() };
}

/**
 * Resolve the Visa Dining Collection preview config for this session.
 *
 * The URL param is the source of truth and, when present, is persisted to
 * sessionStorage so it survives the diners → date → time navigation (which
 * drops query strings). When the param is absent, the value captured earlier
 * this session is used. `?previewVisaDining=0` clears it.
 *
 * Returns "off" in production, where the param is never read or persisted.
 */
export function getVisaDiningPreview(): VisaDiningPreview {
  if (typeof window === "undefined" || !previewAllowed()) return PREVIEW_OFF;

  const raw = new URLSearchParams(window.location.search).get(VISA_DINING_PREVIEW_PARAM);

  if (raw !== null) {
    const parsed = parsePreview(raw);
    if (parsed.count > 0 || parsed.times.size > 0) {
      safeSessionStorage.setItem(STORAGE_KEY, raw);
    } else {
      safeSessionStorage.removeItem(STORAGE_KEY);
    }
    return parsed;
  }

  const stored = safeSessionStorage.getItem(STORAGE_KEY);
  return stored === null ? PREVIEW_OFF : parsePreview(stored);
}

/** True when the slot at `index` with hour `time` should show the badge. */
export function isVisaDiningPreviewSlot(
  preview: VisaDiningPreview,
  time: string,
  index: number,
): boolean {
  return index < preview.count || preview.times.has(time);
}

/**
 * Capture the preview flag from the landing URL once at startup, before SPA
 * navigation strips the query param. Mirrors initReservationAttribution().
 */
export function captureVisaDiningPreview(): void {
  getVisaDiningPreview();
}
